import React from 'react';
import ReactModal from 'react-modal';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled, { css } from 'styled-components';
import Colors from '@components/shared/Colors';
import { Typography, TextVariant } from '@components/shared/design/Typography';
import CloseIcon from '@images/icon-x.svg';
import { useScrollBlock } from '@utils/useScrollBlock';
import TextLinkButton from './TextLinkButton';

const modalHeight = '80vh';

const modalStyles: ReactModal.Styles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '600px',
    maxWidth: '90vw',
    height: 'auto',
    maxHeight: modalHeight,
    padding: '0',
    border: '0',
    overflow: 'hidden',
    borderRadius: '0',
    background: Colors.cream,
    boxShadow: '0 2px 5px rgba(0, 28, 59, 0.3)'
  },
  overlay: {
    background: `${Colors.navyBlue}25`
  }
};

interface Props {
  title?: string;
  modalTopCss?: string;
  modalOpen: boolean;
  onClose?: (a: boolean) => void;
  children?: React.ReactNode;
  stickyHeader?: boolean;
  isWide?: boolean;
  paddingDesktop?: string;
  paddingMobile?: string;
  contentOverflow?: string;
  width?: string;
  includeBottomClose?: boolean;
  headerColor?: string;
  animateTransparentHeader?: boolean;
  modernScrollbar?: boolean;
  bigTitle?: boolean;
  customModalStyles?: ReactModal.Styles;
  fullHeight?: boolean;
}

const Modal: React.FC<Props> = ({
  title,
  modalOpen,
  onClose,
  children,
  isWide,
  modalTopCss = '50%',
  paddingDesktop = '40px 30px',
  paddingMobile = '20px',
  width = '600px',
  contentOverflow = 'auto',
  includeBottomClose = false,
  headerColor = Colors.paleGold25,
  animateTransparentHeader = false,
  modernScrollbar = false,
  bigTitle = false,
  customModalStyles,
  fullHeight = false
}) => {
  const { t } = useTranslation();
  const [blockScroll, allowScroll] = useScrollBlock();
  const [isScrolled, setIsScrolled] = React.useState(false);
  modalStyles.content!.width = width;
  modalStyles.content!.top = modalTopCss;

  if (isWide) modalStyles.content!.width = '750px';

  React.useEffect(() => {
    ReactModal.setAppElement('body');

    return () => {
      allowScroll();
    };
  }, []);

  React.useEffect(() => {
    if (modalOpen) {
      blockScroll();
    } else {
      allowScroll();
    }
  }, [modalOpen]);

  const closeModal = () => {
    if (onClose) {
      onClose(true);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;
    setIsScrolled(scrollTop > 100);
  };

  if (!modalOpen) {
    return null;
  }

  return (
    <div onClick={onClose ? closeModal : undefined}>
      <ReactModal
        isOpen={modalOpen}
        onAfterOpen={() => null}
        onRequestClose={() => null}
        style={customModalStyles || modalStyles}
        contentLabel={title}
      >
        <ModalInner onClick={(e) => e.stopPropagation()}>
          <ModalHeader $headerColor={animateTransparentHeader && !isScrolled ? 'transparent' : headerColor} $hasTitle={!!title}>
            {title && (
              <Typography $variant={bigTitle ? TextVariant.Heading6 : TextVariant.UpperText3} as="h2">
                {title}
              </Typography>
            )}
            {onClose && (
              <button onClick={closeModal} data-testid="modal-close-button">
                <CloseIcon />
              </button>
            )}
          </ModalHeader>
          <ModalBody
            onScroll={handleScroll}
            $animateTransparentHeader={animateTransparentHeader}
            $paddingDesktop={paddingDesktop}
            $paddingMobile={paddingMobile}
            $overflow={contentOverflow}
            $modernScrollbar={modernScrollbar}
            $fullHeight={fullHeight}
          >
            {children}
            {includeBottomClose && onClose && (
              <ModalEndClose>
                <TextLinkButton onClick={closeModal} $color={Colors.navyBlue}>
                  {t('components.close')}
                </TextLinkButton>
              </ModalEndClose>
            )}
          </ModalBody>
        </ModalInner>
      </ReactModal>
    </div>
  );
};

export default Modal;

const ModalInner = styled.div`
  height: 100%;
  overflow: hidden;
`;

const ModalHeader = styled.div<{ $headerColor?: string; $hasTitle?: boolean }>`
  display: flex;
  padding: 20px 32px;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  z-index: 2;
  position: sticky;
  top: 0;
  transition: background-color 0.3s ease;
  background: ${(props) => (props.$hasTitle ? props.$headerColor : 'transparent')};
  height: ${(props) => (props.$hasTitle ? 'auto' : '52px')};

  @media (max-width: 768px) {
    padding: 16px 24px;
  }

  button {
    background: ${(props) => (props.$hasTitle ? 'transparent' : props.$headerColor)};
    border-radius: ${(props) => (props.$hasTitle ? '0' : '50%')};
    position: absolute;
    right: ${(props) => (props.$hasTitle ? '25px' : 'calc(50% - 18px)')};
    top: ${(props) => (props.$hasTitle ? '50%' : '16px')};
    border: 0;
    height: ${(props) => (props.$hasTitle ? '24px' : '36px')};
    width: ${(props) => (props.$hasTitle ? '24px' : '36px')};
    padding: ${(props) => (props.$hasTitle ? '5px0' : '10px')};
    margin-top: ${(props) => (props.$hasTitle ? '-12px' : '0')};
    outline: none;
    cursor: pointer;
    display: flex;
    align-items: center;

    @media (max-width: 768px) {
      ${(props) => (props.$hasTitle ? 'right: 19px;' : '')};
    }

    &:hover {
      opacity: 0.5;
    }

    svg {
      width: 100%;
      height: 100%;

      path {
        fill: ${Colors.navyBlue};
      }
    }
  }
`;

interface IModalBody {
  $animateTransparentHeader?: boolean;
  $paddingDesktop: string;
  $paddingMobile: string;
  $overflow: string;
  $modernScrollbar?: boolean;
  $fullHeight?: boolean;
}

const ModalBody = styled.div<IModalBody>`
  margin-top: ${(props) => (props.$animateTransparentHeader ? '-55px' : '0')};
  padding: ${(props) => props.$paddingDesktop};
  overflow-y: ${(props) => props.$overflow};
  max-height: ${(props) => (props.$fullHeight ? '98vh' : `calc(${modalHeight} - 80px)`)};
  height: auto;

  ${(props) =>
    props.$modernScrollbar &&
    css`
      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.1);
        border-radius: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.3);
        border-radius: 4px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: rgba(0, 0, 0, 0.5);
      }
    `};

  @media only screen and (max-width: 768px) {
    padding: ${(props) => props.$paddingMobile};
  }
`;

const ModalEndClose = styled.div`
  text-align: center;
  margin-top: 40px;
`;
